import React from 'react';
import { X, Save, Loader } from 'lucide-react';

/**
 * Edit Template Modal Component
 * Responsive modal for creating/editing agreement templates
 */
const EditTemplateModal = ({
  isOpen,
  onClose,
  isEditing = false, // true = editing existing, false = creating new
  editForm,
  setEditForm,
  onSave,
  saving = false
}) => {
  if (!isOpen) return null;

  const isValid = editForm.name && editForm.title && editForm.htmlContent;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[95vh] sm:max-h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              {isEditing ? 'Edit Template' : 'Create Template'}
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg -mr-2"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6">
          <div className="space-y-4 sm:space-y-5">
            {/* Template Name & Title - Stack on mobile */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name *
                </label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g., Standard Service Agreement"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Agreement Title *
                </label>
                <input
                  type="text"
                  value={editForm.title}
                  onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  placeholder="e.g., Service Agreement"
                />
                <p className="text-xs text-gray-500 mt-1">Shown to customers</p>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <input
                type="text"
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                placeholder="Brief description for your reference"
              />
            </div>

            {/* HTML Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Agreement Content (HTML) *
              </label>
              
              {/* Merge Fields Info - Collapsible on mobile */}
              <details className="bg-blue-50 border border-blue-200 rounded-lg mb-2">
                <summary className="px-3 py-2 text-sm text-blue-800 cursor-pointer font-medium">
                  Available Merge Fields
                </summary>
                <div className="px-3 pb-2 text-xs text-blue-700 space-y-1">
                  <p><code className="bg-blue-100 px-1 rounded">{'{{CustomerName}}'}</code> - Customer's full name</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{BusinessName}}'}</code> - Your business name</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{JobDescription}}'}</code> - Job description</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{JobDate}}'}</code> - Scheduled date</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{JobTotal}}'}</code> - Total amount</p>
                  <p><code className="bg-blue-100 px-1 rounded">{'{{JobAddress}}'}</code> - Job address</p>
                </div>
              </details>

              <textarea
                value={editForm.htmlContent}
                onChange={(e) => setEditForm({ ...editForm, htmlContent: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs sm:text-sm"
                rows="8"
                placeholder="<h2>Service Agreement</h2>..."
                style={{ minHeight: '150px' }}
              />
            </div>

            {/* Include Sections - Grid on mobile for better space usage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Include Sections
              </label>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-3 gap-2">
                <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={editForm.includeCancellationPolicy}
                    onChange={(e) => setEditForm({ ...editForm, includeCancellationPolicy: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Cancellation</span>
                </label>
                <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={editForm.includeWeatherPolicy}
                    onChange={(e) => setEditForm({ ...editForm, includeWeatherPolicy: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Weather</span>
                </label>
                <label className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
                  <input
                    type="checkbox"
                    checked={editForm.includePhotoConsent}
                    onChange={(e) => setEditForm({ ...editForm, includePhotoConsent: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Photo/Video</span>
                </label>
              </div>
            </div>

            {/* Link Expiration */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Link Expiration
              </label>
              <select
                value={editForm.signatureLinkExpirationDays}
                onChange={(e) => setEditForm({ ...editForm, signatureLinkExpirationDays: parseInt(e.target.value) })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              >
                <option value={3}>3 days</option>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="p-4 sm:p-6 border-t border-gray-200 flex-shrink-0">
          <div className="flex flex-col-reverse sm:flex-row gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || !isValid}
              className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium"
            >
              {saving ? (
                <>
                  <Loader className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Save Changes' : 'Create Template'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditTemplateModal;